import type { CsvRow, Dataset } from './_types';

/** Parse datasets passed from Twig via `data-datasets`. */
export const parseDatasets = (value: string | null): Dataset[] => {
  if (!value) return [];

  try {
    const parsed = JSON.parse(value);
    if (!Array.isArray(parsed)) return [];

    return parsed
      .filter(
        (item) =>
          item &&
          typeof item.id === 'string' &&
          typeof item.file === 'string' &&
          typeof item.name === 'string' &&
          typeof item.status === 'string' &&
          typeof item.color === 'string'
      )
      .map((item) => ({
        ...item,
        polygon: parsePolygonValue(item.polygon)
      })) as Dataset[];
  } catch {
    return [];
  }
};

const parsePolygonValue = (value: unknown): any | null => {
  if (!value) return null;

  const parsed =
    typeof value === 'string'
      ? (() => {
          try {
            return JSON.parse(value);
          } catch {
            return null;
          }
        })()
      : value;

  const geometry = parsed?.geometry || parsed;

  if (
    geometry?.type === 'Polygon' &&
    Array.isArray(geometry.coordinates?.[0]) &&
    geometry.coordinates[0].length >= 4
  ) {
    return geometry;
  }

  return null;
};

/** Parse semicolon-separated CSV text into row objects. */
export const parseCsv = (text: string): CsvRow[] => {
  const lines = text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  if (!lines.length) return [];

  const headers = lines[0].split(';').map((h) => h.trim().toLowerCase());
  const rows: CsvRow[] = [];

  for (let i = 1; i < lines.length; i += 1) {
    const cols = lines[i].split(';').map((c) => c.trim());
    if (!cols.length) continue;

    const row: CsvRow = {};
    for (let j = 0; j < headers.length; j += 1) {
      row[headers[j]] = cols[j] ?? '';
    }

    rows.push(row);
  }

  return rows;
};

/** Parse localized numbers like `354496,516`. */
export const parseNumber = (value: unknown): number | null => {
  if (value == null) return null;

  const normalized = String(value).replace(/\s+/g, '').replace(',', '.');
  const num = Number.parseFloat(normalized);

  return Number.isFinite(num) ? num : null;
};

/** Normalize text for case-insensitive matching and common German address variants. */
export const normalizeText = (value: unknown): string =>
  String(value || '')
    .replace(/\u00a0/g, ' ')
    .toLowerCase()
    .replace(/ß/g, 'ss')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\bstr\./g, 'strasse')
    .replace(/\bstr\b/g, 'strasse')
    .replace(/\bstrasse\b/g, 'strasse')
    .replace(/[.,;:/\\()[\]{}"'`´]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

/** Build a readable one-line address from CSV fields. */
export const buildAddressLabel = (row: CsvRow): string => {
  const street = row['straßenname'] || '';
  const number = row['hausnummer'] || '';
  const suffix = row['hausnummerzusatz'] || '';
  const city = row['stadt'] || '';
  const zip = row['postleitzahl'] || '';

  const streetPart = `${street} ${number}${suffix ? ` ${suffix}` : ''}`.trim();
  const cityPart = `${zip} ${city}`.trim();

  return `${streetPart}${cityPart ? `, ${cityPart}` : ''}`.trim();
};

/** Build a stable key for duplicate detection. */
export const buildAddressKey = (row: CsvRow): string =>
  normalizeText(
    [
      row['stadt'],
      row['postleitzahl'],
      row['straßenname'],
      row['hausnummer'],
      row['hausnummerzusatz']
    ]
      .filter(Boolean)
      .join(' ')
  );

/** Check whether a row matches a free-text address query. */
export const rowMatchesAddressQuery = (query: string, row: CsvRow): boolean => {
  const q = normalizeText(query);
  if (!q) return false;

  const shortLabel = normalizeText(
    `${row['straßenname'] || ''} ${row['hausnummer'] || ''} ${row['hausnummerzusatz'] || ''}`
  );
  const fullLabel = normalizeText(buildAddressLabel(row));

  return shortLabel === q || fullLabel === q || fullLabel.includes(q);
};

/**
 * Find a row by exact address key or by partial full-address match.
 */
export const findRowByQuery = (
  query: string,
  rows: CsvRow[]
): CsvRow | null => {
  for (const row of rows) {
    if (rowMatchesAddressQuery(query, row)) return row;
  }

  return null;
};

/**
 * Generic variant that preserves extended row fields (e.g. `_dataset`).
 */
export const findRowByQueryWithType = <T extends CsvRow>(
  query: string,
  rows: T[]
): T | null => findRowByQuery(query, rows) as T | null;
